import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { OrderType, TotalsPrefix } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup, SortOrder } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxFor } from '@rx-angular/template/for';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionAttributeNameStrategy } from './option-attribute-name.strategy';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionPickPlaceholderComponent } from './option-pick-placeholder.component';
import { OptionComponent } from './option.component';
import { BaseOptionGroupStatisticsService } from './player-statistics/base-option-group-statistics.service';
import {
    IPlayerPropsOptionLine,
    OptionGroupPlayerStatisticsService,
    StatisticsParams,
} from './player-statistics/option-group-player-statistics.service';
import { PlayerStatisticsComponent } from './player-statistics/player-statistics.component';
import { PlayerStatisticsService, SeasonStatisticsType } from './player-statistics/player-statistics.service';
import { PlayerStatsComponent } from './player-stats/player-stats.component';
import { SplitHeaderComponent } from './split-header/split-header.component';
import { TeamGroupingTabBarComponent } from './team-grouping/team-grouping-tab-bar.component';

@Component({
    selector: 'ms-player-props-option-group',
    templateUrl: './player-props-option-group.component.html',
    styleUrl: './player-props-option-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: OptionNameStrategy,
            useClass: OptionAttributeNameStrategy,
        },
        ParticipantImageService,
        OptionGroupPlayerStatisticsService,
        BaseOptionGroupStatisticsService,
    ],
    imports: [
        RxFor,
        NgTemplateOutlet,
        MarketHelpComponent,
        TeamGroupingTabBarComponent,
        PlayerStatsComponent,
        OptionComponent,
        OptionPanelBottomActionComponent,
        SplitHeaderComponent,
        ParticipantImageComponent,
        OptionPickPlaceholderComponent,
        PlayerStatisticsComponent,
        IconCustomComponent,
    ],
})
export class PlayerPropsOptionGroupComponent implements OnInit, OnChanges {
    sitecore = inject(Sitecore);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private optionGroupService = inject(OptionGroupService);
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private participantImageService = inject(ParticipantImageService);

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    readonly optionGroupPlayerStatisticsService = inject(OptionGroupPlayerStatisticsService);

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick: SourceOfPick;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() showPlayerStats = false;

    @Output() showAllRequested = new EventEmitter<void>();

    @ViewChild('optionGroupContainer') container: ElementRef<HTMLElement>;
    @ViewChild('splitHeaderComponent') splitHeaderComponent: SplitHeaderComponent;

    _optionsLimitOverride = signal<number | undefined>(undefined);
    optionLines = signal<IPlayerPropsOptionLine[]>([]);

    optionLines$ = toObservable(
        computed(() => {
            if (this.visibleOptionsLimit() == null) {
                return this.optionLines();
            }

            return this.optionLines().filter((_, i) => i < this.visibleOptionsLimit()!);
        }),
    );

    overHeader: string;
    underHeader: string;

    expanded = signal(false);
    showMoreLessButtonVisible = computed(() => this.optionLines().length > (this._optionsLimitOverride() ?? this.teamGroupingMaxNumberOfLines));
    visibleOptionsLimit = computed(() => {
        return this.isInSheetView || this.expanded() ? undefined : (this.optionsLimitOverride ?? this.teamGroupingMaxNumberOfLines);
    });

    visibleOptionGroups = computed(() => {
        let length = this.optionLines().length;
        if (this.visibleOptionsLimit() != null) {
            length = Math.min(this.visibleOptionsLimit()!, length);
        }

        return length;
    });

    @HostBinding('class') className = 'player-props-option-group';

    teamGroupingMaxNumberOfLines = this.marketGroupingConfig.teamGroupingMaxNumberOfLines;
    private _teamGroupingTabVisibilityThreshold = this.marketGroupingConfig.teamGroupingTabVisibilityThreshold;
    isTeamGroupingEnabled = false;
    controllerTabName: string;
    isShowHeadshotImage: boolean;
    participantType = ParticipantType;

    showMoreData: ShowMoreData;
    sourcesOfPick: SourceOfPick[];
    playerStatisticsService = inject(PlayerStatisticsService);
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;
    readonly seasonStatisticsMessage = this.playerStatisticsService.seasonStatisticsMessage;
    readonly SeasonStatisticsType = SeasonStatisticsType;
    readonly OptionGroupType = OptionGroupType;

    ngOnInit(): void {
        this.controllerTabName =
            this.sitecore.teamGrouping[this.optionGroup.detailedGrouping.teamGrouping! as keyof typeof this.sitecore.teamGrouping];
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.overHeader = this.optionGroup.parameterTranslations?.columnOver ?? '';
        this.underHeader = this.optionGroup.parameterTranslations?.columnUnder ?? '';

        if (changes.optionsLimitOverride) {
            this._optionsLimitOverride.set(this.optionsLimitOverride);
        }

        if (changes.event || changes.optionGroup || changes.sourceOfPick) {
            if (
                changes.optionGroup?.previousValue !== changes.optionGroup?.currentValue ||
                changes.event?.previousValue !== changes.event?.currentValue
            ) {
                this.optionGroupPlayerStatisticsService.initializePlayerStatistics(this.event, OptionGroupType.PlayerProps);
                this.optionGroupPlayerStatisticsService.fetchPlayerStatistics(this.event, this.optionGroup);
            }
            this.optionLines.set(this.getOptionLines(this.optionGroup));
            this.setProperties();
            this.sourcesOfPick = [];
            this.sourcesOfPick.push(changes.sourceOfPick?.currentValue);

            if (this.isTeamGroupingEnabled) {
                this.sourceOfPick = SourceOfPick.TeamGrouping;
                this.sourcesOfPick.push(SourceOfPick.TeamGrouping);
            }
            this.isShowHeadshotImage = this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.PlayerProps);
        }

        if (changes.event || changes.optionGroup || changes.optionsLimitOverride) {
            const totalCount = this.optionLines().length;
            this.showMoreData = {
                totalCount,
                initialVisibleCount: this.optionsLimitOverride ?? this.teamGroupingMaxNumberOfLines,
                pageSize: totalCount,
            };
        }
    }

    trackOptionLine(index: number, optionLine: IPlayerPropsOptionLine): string {
        return `${optionLine.overOption?.id}-${optionLine.underOption?.id}`;
    }

    trackGroup(index: number, group: OptionGroup): string {
        return group.attribute || '';
    }

    isMarketPromoted(optionGroupId: number, templateId: number): boolean {
        return !!this.crmPromoIdsMap.markets[optionGroupId] || (templateId != null && !!this.crmPromoIdsMap.templateIds[templateId]);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded.set(state !== 'collapsed');

        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded() ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            'players',
        );
    }

    getPlayers(optionGroup: IPlayerPropsOptionLine): number[] {
        const players: number[] = [];

        if (optionGroup.playerId) {
            players.push(optionGroup.playerId);
        }

        return players;
    }

    onTeamGroupingChange(teamGrouping: { optionGroup: EventOptionGroup; controllerTabName?: string }): void {
        this.optionLines.set(this.getOptionLines(teamGrouping.optionGroup));
        this.controllerTabName = teamGrouping.controllerTabName ?? this.controllerTabName;
        this.changeDetectorRef.detectChanges();
        this.splitHeaderComponent.scrollToTop();
    }

    getTracking(optionLine: IPlayerPropsOptionLine) {
        return {
            source: this.tracking,
            moduleType: this.trackingModuleType,
            controllerTabName: this.controllerTabName,
            playersName: optionLine.playerName,
        };
    }
    togglePlayerStatistics(item: IPlayerPropsOptionLine): void {
        const params = this.extractParams(item);
        this.optionGroupPlayerStatisticsService.toggleOpen(this.event, params, this.optionGroup, OptionGroupType.PlayerProps);
    }

    isPlayerStatisticsOpen(item: IPlayerPropsOptionLine): boolean {
        const params = this.extractParams(item);
        return this.optionGroupPlayerStatisticsService.isOpen(params);
    }

    private extractParams(item: IPlayerPropsOptionLine): StatisticsParams {
        const optionGroupId = (item.overOption?.optionGroupId ?? item.underOption?.optionGroupId)?.toString();
        const optionId = (item.overOption?.id ?? item.underOption?.id)?.toString();
        return {
            optionGroupId,
            playerName: item.playerName,
            key: optionGroupId,
            optionId,
        };
    }

    private getPlayerName(option: EventOption): string {
        const marketId = option.optionGroupId;
        const optionGroups = this.event.marketsDictionary[marketId];

        return (optionGroups && optionGroups.length && optionGroups[0].player1) || '';
    }

    private getOptionLines(optionGroup: EventOptionGroup): IPlayerPropsOptionLine[] {
        const orderByOptionAttribute = this.marketGroupingConfig.orderByLineValue && optionGroup.detailedGrouping.orderType === OrderType.LineValue;

        const optionGroups = this.optionGroupService.getOptionGroups(
            optionGroup,
            (option) => option.optionGroupId,
            orderByOptionAttribute ? (o) => o.optionAttribute : undefined,
            orderByOptionAttribute ? SortOrder.Descending : undefined,
        );

        const options = optionGroups.flatMap((group) => group.options);
        const hasTotalPrefix = options.every((o) => !!o.totalsPrefix);
        let optionLines;
        if (hasTotalPrefix) {
            optionLines = optionGroups.reduce<IPlayerPropsOptionLine[]>((previous, current, index) => {
                const overOption = optionGroups[index].options.find((option) => option.totalsPrefix === TotalsPrefix.Over);
                const underOption = optionGroups[index].options.find((option) => option.totalsPrefix === TotalsPrefix.Under);
                previous.push({
                    playerId: optionGroups[index].options[0].player1Id,
                    playerName: this.getPlayerName(optionGroups[index].options[0]),
                    overOption,
                    underOption,
                });

                return previous;
            }, []);
        } else {
            optionLines = options.reduce<IPlayerPropsOptionLine[]>((previous, current, index) => {
                if (index % 2 === 0) {
                    const overOption = options[index];
                    const underOption = options[index + 1];
                    previous.push({
                        playerId: options[index].player1Id,
                        playerName: this.getPlayerName(options[index]),
                        overOption,
                        underOption,
                    });
                }

                return previous;
            }, []);
        }
        return optionLines;
    }

    private setProperties(): void {
        this.isTeamGroupingEnabled = this.optionGroupService.isTeamGroupingEnabled(
            this.event,
            this.optionGroup,
            this._teamGroupingTabVisibilityThreshold,
        );
    }
}
